import React, { useState, useEffect, useRef } from "react";
import { IRoomCardProps } from "./RoomCardProps";
import {
  StyledRoomCard,
  StyledModal,
  PromoBadge,
  UrgencyBadge,
} from "./RoomCardHelpers";
import defaultRoomImage from "../../../images/hotel_fallback.jpg";
import Card from "react-bootstrap/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import UnlockPriceButton from "../UnlockPriceButton/UnlockPriceButton";
import { formatRedemptionItem, priceFormatter } from "../../../utils/helpers";
import { useUnlockDiscount } from "../../../hooks/useUnlockDiscount";
import { NewImageCarousel } from "../ImageCarousel/ImageCarousel";
import { useAppSelector } from "../../../hooks";
import { searchSelector } from "../../../redux/slices/Search/search";
import { useSelector } from "react-redux";
import queryString from "query-string";
import IURLSearchParams from "../../../@types/IURLSearchParams";
import { useLocation } from "@reach/router";
import { useWebFramed } from "../../../hooks/useWebFramed";
import { unlockPriceCta } from "./RoomCard.module.scss";
import { isLoggedInSelector } from "../../../redux/slices/Member/member";
import { TP_REDEMPTION_RATE } from "../../../@types/Constants";
import { useRedemption } from "../../../hooks/useRedemption";
import { fireMemberRatePresent } from "../../../utils/datalayers";
import InfoIconTooltip from "../InfoIconTooltip/InfoIconTooltip";
const RoomCard: React.FC<IRoomCardProps> = (props) => {
  const location = useLocation();
  const isWebFramed = useWebFramed();
  const memberLoggedIn = useAppSelector(isLoggedInSelector);
  const recentSearch = useAppSelector((state: any) => state.recentSearch);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const {
    room,
    selectRoom,
    loadingRates,
    isGroupCodeValid,
    selected = false,
    page,
    roomCardId,
    redemptionItem = {},
    hotel,
    currentRoom,
    isSearchResult,
    checkoutDiscountKey,
    isCheckoutPage,
  } = props;
  const roomImage =
    room.imageUrls && room.imageUrls.length
      ? room.imageUrls[0]
      : defaultRoomImage;

  const carouselImages =
    room?.galleryImages && room.galleryImages?.length
      ? room.galleryImages
      : room.imageUrls && room.imageUrls.length
      ? room.imageUrls.map((url: string) => ({ url, alt: url }))
      : null;

  const bestRate = room?.FromRateWithFees;
  const lowRoomCount =
    room.quantity > 0 && room.quantity <= 5 ? room.quantity : false;
  const isRoomAvailable = room.quantity > 0;
  const [expanded, setExpanded] = useState(false);
  const [showZoomedImage, setShowZoomedImage] = useState(false);
  const [
    showUnlockButton,
    showMemberRateLabel,
    _discountUnlocked,
    showSpecialPricing,
  ] = useUnlockDiscount(room.FromRateType);
  const currency = room.Currency;

  const search = useAppSelector(searchSelector);
  const checkout = useSelector((state: any) => state.editReservationCheckout);
  let promo = null;
  if (page === "edit-reservation") {
    promo = checkout.promotionCode || checkout.groupCode;
  } else {
    promo = search.promotionCode || search.groupCode;
  }
  const showPromoBadge = promo && room.FromRatePromotional;

  const handleSetExpanded = () => {
    setExpanded(!expanded);
  };

  const renderedPrice =
    !loadingRates &&
    (!showUnlockButton ||
      showSpecialPricing ||
      TP_REDEMPTION_RATE.includes(room?.BaseRateCode))
      ? bestRate
      : room?.BaseRateWithFees;
  const renderedFeesText =
    !loadingRates &&
    (!showUnlockButton ||
      showSpecialPricing ||
      TP_REDEMPTION_RATE.includes(room?.BaseRateCode))
      ? room.FromRateFees
      : room?.BaseRateFees;
  const renderedFeeItems =
    !loadingRates &&
    (!showUnlockButton ||
      showSpecialPricing ||
      TP_REDEMPTION_RATE.includes(room?.BaseRateCode))
      ? room.FromRateFeeItems
      : room?.FeeItems;
  const isRedemptionRate = useRedemption({
    room: room,
    hotelCode: props.hotel?.crs_code,
  });

  const isCurrentRoom = currentRoom && room.code === currentRoom;

  useEffect(() => {
    if (location.search.length) {
      const qp = queryString.parse(location.search, {
        parseBooleans: true,
        arrayFormat: "index",
      });
      const queryParams = Object.keys(qp).reduce((_qp, key) => {
        _qp[key.toLowerCase()] = qp[key];
        return _qp;
      }, {} as IURLSearchParams);
      const roomIndex = props.roomIndex;
      const roomTypeCode = queryParams["roomtypecode"]?.split(",");
      const roomCode = roomTypeCode && roomTypeCode[roomIndex];
      if (room?.RoomCode && room?.RoomCode === roomCode) {
        buttonRef?.current?.click();
      }
    }
  }, [isWebFramed, location.search, recentSearch, room?.RoomCode]);

  useEffect(() => {
    if (room?.FromRateType === "member") {
      const pageType =
        location?.pathname === "/checkout"
          ? "Checkout Select Room"
          : "Hotel Details";
      fireMemberRatePresent(pageType, room);
    }
  }, [room.FromRateType]);

  const hasLongPrice = renderedPrice?.toString().split(".")[0].length > 3;
  const isCurrencyCAD = room?.Currency === "CAD";
  const isPriceLong = hasLongPrice || isCurrencyCAD;
  const isPriceLongAndCAD = hasLongPrice && isCurrencyCAD;

  const queryParams = queryString.parse(location.search, {
    parseBooleans: true,
    arrayFormat: "index",
  });

  const finalDiscount = isCheckoutPage
    ? checkoutDiscountKey
    : queryParams?.ratePlanFilterCode || search.discount;

  const isPackageDiscount = finalDiscount === "PKG";
  const isAAADiscount = finalDiscount === "AAA";
  const isGovDiscount = finalDiscount === "GOV";

  const onlyTPRNR =
    !loadingRates &&
    room?.Rates?.length === 1 &&
    room.Rates[0].rateCode === "TPRNR";

  const showPackageRate =
    !loadingRates && isPackageDiscount && isSearchResult && !onlyTPRNR;
  const showAAARate =
    !loadingRates && isAAADiscount && isSearchResult && !onlyTPRNR;
  const showGovRate =
    !loadingRates && isGovDiscount && isSearchResult && !onlyTPRNR;

  const showMemberRate =
    !loadingRates &&
    showMemberRateLabel &&
    !showPackageRate &&
    !showAAARate &&
    !showGovRate;

  return isRoomAvailable ? (
    <>
      <StyledRoomCard
        className="border-0 text-left room-card"
        id={"room-card-" + roomCardId}
        isCurrentRoom={isCurrentRoom}
      >
        {isCurrentRoom && <div className="current-room-head">Current Room</div>}
        <div className={selected ? "selected" : ""}>
          <div
            className={`image-wrapper ${
              isCurrentRoom ? "current-room-image" : ""
            }`}
            id="room-image"
          >
            <Card.Img variant="top" src={roomImage} alt={room.name} />

            {showPromoBadge ? (
              <PromoBadge id={"promo-badge"}>
                <span>{promo.toLocaleUpperCase()}</span> Applied
              </PromoBadge>
            ) : null}
            {lowRoomCount && !isGroupCodeValid && (
              <UrgencyBadge
                id={"urgency-badge"}
                style={{
                  backgroundColor: props.hotelInfoUrgBgColor,
                  color: props.hotelInfoUrgFontColor,
                }}
              >
                {`Only ${lowRoomCount} ${
                  lowRoomCount > 1 ? "Rooms" : "Room"
                } Left!`}
              </UrgencyBadge>
            )}

            {carouselImages && carouselImages.length > 1 && (
              <svg
                id="gallery-icon"
                className="icon"
                onClick={() => {
                  setShowZoomedImage(true);
                }}
              >
                <use xlinkHref="/svg/gallery-icon.svg#gallery-icon"></use>
              </svg>
            )}
          </div>
          <Card.Body
            className={`d-lg-flex justify-content-lg-between flex-lg-column pt-2 ${
              isCurrentRoom ? `room-card-body-currentRoom` : ""
            }`}
          >
            <div className="mb-2 name-container">
              <div className="room-name">
                <h4
                  style={{ fontWeight: 700 }}
                  id="room-name"
                  className=" d-table-cell name align-middle border-right"
                >
                  {room.name}
                </h4>
                <div className="occupancy mb-2" id="guest-occupancy">
                  Up to {room.occupancy} guests
                </div>
              </div>
              <div className="expanded-btn">
                <Button
                  className={`details  ${expanded ? "expanded" : ""}`}
                  variant="secondary"
                  id={`room-description-show-more-button`}
                  onClick={handleSetExpanded}
                  aria-label={!expanded ? "Show More" : "Hide More"}
                  aria-expanded={expanded}
                >
                  {expanded ? (
                    <FontAwesomeIcon
                      style={{ pointerEvents: "none" }}
                      icon="chevron-up"
                    />
                  ) : (
                    <FontAwesomeIcon
                      style={{ pointerEvents: "none" }}
                      icon="chevron-down"
                    />
                  )}
                </Button>
              </div>
            </div>
            <div
              className={`description mb-3 mt-2 ${
                expanded ? "d-block" : "d-none"
              }`}
              id="room-description"
              dangerouslySetInnerHTML={{ __html: room.description }}
            />
            <div className="rate-container">
              {showMemberRate && <div className="rate-label">MEMBER RATE</div>}
              {showAAARate && (
                <div className="rate-label">AAA / SENIOR RATE</div>
              )}
              {showGovRate && <div className="rate-label">GOVERNMENT RATE</div>}
              {showPackageRate && (
                <div className="rate-label">PACKAGE RATE</div>
              )}
            </div>
            <div
              className={
                isPriceLongAndCAD
                  ? "CAPriceWrapper"
                  : isPriceLong
                  ? memberLoggedIn
                    ? "LongPriceWrapper tp-pointsWrapper"
                    : "LongPriceWrapper"
                  : memberLoggedIn
                  ? "PriceWrapper tp-pointsWrapper"
                  : "PriceWrapper"
              }
            >
              {loadingRates ? (
                <div className="spinner">
                  <Spinner id="rate-spinner" animation="border" />
                </div>
              ) : (
                renderedPrice && (
                  <>
                    <div className="d-flex align-items-center gap-1">
                      <span className="price">
                        {priceFormatter(renderedPrice, 0, currency)}
                      </span>
                      {process.env.GATSBY_SHOW_MEMEBER_RATE_LABEL === "true" &&
                      memberLoggedIn
                        ? ""
                        : (!showUnlockButton ||
                            showSpecialPricing ||
                            (!showPromoBadge && showMemberRateLabel)) &&
                          room?.BaseRateWithFees - renderedPrice > 0.5 && (
                            <span id="old-price" className="strikethrough">
                              <span className="visuallyhidden">{`Old Price`}</span>
                              {room?.BaseRateWithFees &&
                                priceFormatter(
                                  room?.BaseRateWithFees,
                                  0,
                                  currency
                                )}
                            </span>
                          )}
                      <span className="pernight-label">
                        per night{renderedFeesText > 0 ? " with fees" : ""}
                        {renderedFeeItems?.length > 0 && (
                          <InfoIconTooltip content={renderedFeeItems} />
                        )}
                      </span>
                    </div>
                    {isRedemptionRate && (
                      <>
                        <h4 className="tp-points">
                          or{" "}
                          {formatRedemptionItem(
                            redemptionItem?.currencyRequired
                          )}{" "}
                          Travel Pass points per night
                        </h4>
                      </>
                    )}
                  </>
                )
              )}
            </div>

            <Button
              className={`w-100 select-room ${
                page !== "edit-reservation" ? "text-uppercase" : ""
              }`}
              size="lg"
              id={`select-room-button`}
              data-room={room.RoomCode}
              data-isunlockbutton={!showSpecialPricing && showUnlockButton}
              onClick={selectRoom}
              aria-label={
                isRoomAvailable
                  ? `Select Room, ${room.name}`
                  : `SOLD OUT, ${room.name}`
              }
              disabled={loadingRates || !isRoomAvailable}
              data-roomprice={renderedPrice}
              ref={buttonRef}
              style={
                page === "edit-reservation"
                  ? { fontSize: "16px", fontWeight: "700" }
                  : {}
              }
            >
              {isRoomAvailable ? "Select Room" : "SOLD OUT"}
            </Button>
            {page !== "edit-reservation" && showUnlockButton && (
              <div className={unlockPriceCta}>
                <UnlockPriceButton />
              </div>
            )}
          </Card.Body>
        </div>
      </StyledRoomCard>
      <StyledModal
        show={showZoomedImage}
        onHide={() => setShowZoomedImage(false)}
        size="lg"
        centered
        aria-label="Room Gallery"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Room Gallery
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewImageCarousel
            activeImage={0}
            images={carouselImages}
            isRoomCard={true}
          />
        </Modal.Body>
      </StyledModal>
    </>
  ) : null;
};

export default RoomCard;
